import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, ArrowRight, Star } from "lucide-react";
import { COMPANY, IMAGES } from "../data";

const LINE = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.9, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span variants={LINE} custom={i} initial="hidden" animate="show" className="block">
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { k: "100%", v: "Gas Safe Registered" },
  { k: "5.0★", v: "Google Rating" },
  { k: "24/7", v: "Emergency Response" },
];

export const Hero = ({ onQuote }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden">
      <div className="absolute inset-0 hero-glow" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-28 sm:pt-36 pb-16 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-8 items-center min-h-screen">
        {/* Left */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 mb-7"
          >
            <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
            <span className="font-mono-sx text-[11px] uppercase tracking-[0.25em] text-zinc-300">
              Available now · {COMPANY.area}
            </span>
          </motion.div>

          <h1 className="font-display font-extrabold uppercase leading-[0.92] tracking-tight text-4xl sm:text-6xl lg:text-7xl">
            <MaskLine i={0}>Your local</MaskLine>
            <MaskLine i={1}>
              <span className="flame-text">heating & plumbing</span>
            </MaskLine>
            <MaskLine i={2}>experts</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-7 max-w-md text-lg text-zinc-400 leading-relaxed"
          >
            Reliable. Professional. Local. Keeping your home warm, comfortable and
            running smoothly — right across Essex.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={onQuote}
              data-testid="hero-quote-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-[#f5641e] px-7 py-4 font-bold text-[#0a0a0c] hover:bg-[#ff7a38] transition-colors"
            >
              Get a Free Quote
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href={`tel:${COMPANY.phoneIntl}`}
              data-testid="hero-call-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-4 font-bold text-white hover:border-[#f5641e]/60 transition-colors"
            >
              <Phone className="h-4 w-4 text-[#f5641e]" />
              {COMPANY.phone}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="mt-12 grid grid-cols-3 gap-4 max-w-lg"
          >
            {STATS.map((s) => (
              <div key={s.v} data-testid={`hero-stat-${s.v.split(" ")[0].toLowerCase()}`} className="border-l border-white/10 pl-4">
                <div className="font-display text-2xl sm:text-3xl font-extrabold text-white">{s.k}</div>
                <div className="mt-1 font-mono-sx text-[10px] uppercase tracking-widest text-zinc-500 leading-tight">{s.v}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right — clipped parallax product frame */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="relative hidden lg:block"
        >
          <div className="relative aspect-[4/5] w-full overflow-hidden rounded-[2rem] border border-white/10 spotlight">
            <motion.img
              src={IMAGES.heroBoiler}
              alt="SX Heating engineer servicing a boiler"
              style={{ y: yImg, scale: scaleImg }}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c] via-transparent to-transparent" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
            className="absolute -bottom-5 -left-5 rounded-2xl border border-white/10 bg-[#121217]/90 backdrop-blur px-5 py-4"
          >
            <div className="flex items-center gap-1 text-[#ffb800]">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4" fill="currentColor" />)}
            </div>
            <p className="mt-1 font-mono-sx text-[10px] uppercase tracking-widest text-zinc-400">
              Rated 5.0 by Essex homeowners
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
